/** @param {unknown} value */
const sanitizeImagePath = (value) =>
  typeof value === "string" ? value.trim().replace(/^\/+/, "") : "";

/**
 * Strip ensureUniqueMediaFilename suffixes (`logo-1.png` → `logo.png`) so CDN
 * hits the real object when Media.filename was uniquified for path aliases.
 * @param {string} filename
 */
const cdnBasenameFromMediaFilename = (filename) => {
  const match = filename.match(/^(.*)-(\d+)(\.[^.]+)$/);
  if (!match) return filename;
  const num = Number(match[2]);
  if (!(num > 0 && num < 1000)) return filename;
  return `${match[1]}${match[3]}`;
};

/** @param {unknown} value */
const imagePathFromUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";
  const trimmed = value.trim();

  let path = "";
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const { pathname } = new URL(trimmed);
      path = pathname
        .replace(/^\/+/, "")
        .replace(/^pgfe\/img\//i, "")
        .replace(/^fit-in\/[^/]+\//i, "");
    } catch {
      return "";
    }
  } else {
    path = trimmed
      .replace(/^\/+/, "")
      .replace(/^pgfe\/img\//i, "")
      .replace(/^fit-in\/[^/]+\//i, "");
  }

  // Alias media rows may expose …/file-1.png while CDN serves …/file.png
  const parts = path.split("/");
  const last = parts[parts.length - 1];
  if (last) {
    parts[parts.length - 1] = cdnBasenameFromMediaFilename(last);
  }
  return parts.join("/");
};

/** @param {unknown} value */
const isLocaleMap = (value) =>
  Boolean(
    value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      ("en" in value || "fr" in value) &&
      !("url" in value) &&
      !("filename" in value) &&
      !("id" in value && ("mimeType" in value || "filesize" in value)),
  );

/**
 * Resolve a Payload upload field (id | media object | `{en,fr}` map) to a CDN-relative path.
 * Numeric IDs alone cannot be resolved here — live preview must hydrate them first.
 *
 * Note: uniquify stripping can mangle date-like suffixes (e.g. `Previews-25-01-15.jpg`).
 * Prefer explicit CMS path fields when available.
 *
 * @param {unknown} upload
 * @param {string} [locale]
 * @returns {string|undefined}
 */
export const resolvePayloadMediaPath = (upload, locale = "en") => {
  if (upload == null) return undefined;

  if (isLocaleMap(upload)) {
    const picked = upload[locale] ?? upload.en ?? upload.fr;
    return resolvePayloadMediaPath(picked, locale);
  }

  if (typeof upload !== "object" || Array.isArray(upload)) return undefined;

  if (typeof upload.url === "string" && upload.url.trim()) {
    const fromUrl = imagePathFromUrl(upload.url);
    if (fromUrl) return fromUrl;
  }

  if (typeof upload.filename === "string" && upload.filename.trim()) {
    const filename = cdnBasenameFromMediaFilename(
      sanitizeImagePath(upload.filename),
    );
    const prefix = sanitizeImagePath(
      upload.prefix || upload.folder || upload.directory || "",
    );
    return prefix ? `${prefix}/${filename}` : filename;
  }

  return undefined;
};
